import { Suspense, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Box } from '@mui/material';
import Stack from '@mui/material/Stack';

import { MeaningfulPaintIntervalType } from '@watershed/constants/monitoring';
import { ProjectFlowMode } from '@watershed/constants/projects';
import { Teams } from '@watershed/constants/teams';
import PageContainer from '@watershed/shared-frontend/components/PageContainer';
import { useOwner } from '@watershed/shared-frontend/hooks/useOwner';
import {
  MEANINGFUL_PAINT_PAGE_NAME,
  useStartMeaningfulPaintTimer,
  useStopMeaningfulPaintTimerAndReportToHoneycomb,
} from '@watershed/shared-frontend/utils/frontendPerformanceMonitoring/measureMeaningfulPaint';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Skeleton from '@watershed/ui-core/components/Skeleton';

import ProjectsListSidebar from '../measure/ProjectsListSidebar';
import FlexibleProjectPageActions from './FlexibleProjectPageActions';
import { useProjectCreationFlowState } from './utils/projectHelperHooks';
import FlexibleProjectsContent from './FlexibleProjectsContent';
import ProjectSettingsFlow from './settingsFlow/ProjectSettingsFlow';

/**
 * This will stop the FLI timer the parent started when the page is rendered.
 */
function useObservabilitySkeletonLogging() {
  useStopMeaningfulPaintTimerAndReportToHoneycomb({
    pageName: MEANINGFUL_PAINT_PAGE_NAME.DI_MEASUREMENT_PROJECTS_PAGE,
    intervalType: MeaningfulPaintIntervalType.FirstLoadingInterval,
  });

  // Starts timer when table loading spinner begins, until table fully loaded.
  useStartMeaningfulPaintTimer({
    pageName: MEANINGFUL_PAINT_PAGE_NAME.DI_MEASUREMENT_PROJECTS_PAGE,
    owner: Teams.DataIngestion,
    intervalType: MeaningfulPaintIntervalType.FirstMeaningfulInterval,
  });
}

/**
 * This is the page containing all flexible "projects" which include:
 * - Legacy measurement projects
 * - [Future] New projects
 * - [Future] Saved views
 */
export default function FlexibleProjectsPage() {
  useOwner(Teams.DataIngestion);
  useObservabilitySkeletonLogging();

  const [connectionId, setConnectionId] = useState<string | null>(null);
  const { isCreateFlowOpen, closeCreateFlow } = useProjectCreationFlowState();

  return (
    <>
      <PageContainer
        heading={{
          title: t({
            message: 'Measurements',
            context: 'Page title for measurements',
          }),
        }}
        headingRightCol={<FlexibleProjectPageActions />}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 320px',
            height: '100%',
            backgroundColor: (theme) => theme.palette.grey05,
          }}
        >
          <Box padding={2} sx={{ display: 'grid', height: 'fit-content' }}>
            <Suspense fallback={<ProjectsSkeleton />}>
              <FlexibleProjectsContent setConnectionId={setConnectionId} />
            </Suspense>
          </Box>
          <ProjectsListSidebar />
        </Box>
      </PageContainer>
      {/* Dialog rendered at page level to ensure only one instance exists,
          regardless of whether user clicks from header or blank slate */}
      {isCreateFlowOpen && (
        <ProjectSettingsFlow
          flowMode={ProjectFlowMode.Create}
          onClose={closeCreateFlow}
          connectionId={connectionId}
        />
      )}
    </>
  );
}

/**
 * Skeleton with the shape of a small data grid w/ filters & ~2 items in length.
 */
function ProjectsSkeleton() {
  return (
    <Stack
      padding={2}
      gap={1}
      data-testid={TestIds.FlexibleProjectsTableSkeleton}
    >
      <Skeleton variant="rounded" animation="wave" height={50} />
      <Skeleton
        variant="rounded"
        animation="wave"
        // 81 px for header bar (57px height + 24px padding)
        // 44px for grid toolbar
        // 16px at the bottom
        // 32px for search bar

        // 81 + 44 + 16 + 32 = 173px
        height="calc(100dvh - 173px)"
      />
    </Stack>
  );
}
